import {isMinimalProductBroken, type RawMinimalProduct} from '@/models/productModel'
import clearQuotes from '@/utils/clearQuotes'

import mergeEscapeStrings from './mergeEscapeStrings'

const productBeginTexts = '```csv\n'

const productNavbarBeginTexts = ['retailer', '"retailer']

export interface RawProductData {
  type: 'products'
  data: RawMinimalProduct[]
}

export default function splitProductsFromMessage(
  message: string,
  header: string | false = false,
): (string | RawProductData)[] {
  if (!header) {
    // Looking for begin of products
    const beginProductPos = message.indexOf(productBeginTexts)
    if (beginProductPos !== -1) {
      // Found products section
      const messageBeforeProducts = message.slice(0, beginProductPos)
      const messageFromProducts = message.slice(beginProductPos + productBeginTexts.length)
      // If the next line is product headers, then continue the process
      if (productNavbarBeginTexts.map(text => messageFromProducts.startsWith(text)).some(Boolean)) {
        // Get message after header: which excluded 1 line after the begin of products
        const header = messageFromProducts.slice(0, messageFromProducts.indexOf('\n'))
        // console.log('Navbar', header)
        const messageAfterNavbar = messageFromProducts.slice(
          messageFromProducts.indexOf('\n') + 1 || message.length,
        )
        // console.log('Before', [messageBeforeProducts])
        // console.log('After', [messageAfterNavbar])
        // console.log('Found products section', [message])
        // Found products section to keep parsing the message from after the header
        return [
          messageBeforeProducts,
          ...splitProductsFromMessage(messageAfterNavbar, header),
        ].filter(Boolean)
      }
      // Then this is not a products section, leave the text as it is, parsing from after the ending
      // console.log('Found falsy products')
      let pos = messageFromProducts.indexOf('```\n') + 4
      if (pos === 3) {
        pos = messageFromProducts.indexOf('```') + 3
      }
      if (pos === 2) {
        pos = messageFromProducts.length - 1
      }
      const messageFromFalsyProductsToEndOfFalsyProducts =
        productBeginTexts + messageFromProducts.slice(0, pos)

      const leftOverMessage = messageFromProducts.slice(pos)
      return [
        messageBeforeProducts + messageFromFalsyProductsToEndOfFalsyProducts,
        ...splitProductsFromMessage(leftOverMessage),
      ]
    }
    // cannot found any products section
    // console.log('Cannot find any more', [message])
    return [message].filter(Boolean)
  }
  // Already found and remove the header
  const testText = message
  const testTextLineEnding = testText.indexOf('\n')

  if (testTextLineEnding !== -1) {
    if (testText.slice(0, testTextLineEnding) === '```') {
      // Found products ending
      // console.log('End products section', [testText])
      const messageAfterProduct = message.slice(4)
      return splitProductsFromMessage(messageAfterProduct, false)
    }
    // Found product ending
    // console.log('Found product', [testText])
    const productText = testText.slice(0, testTextLineEnding)
    const productTextSplited = mergeEscapeStrings(productText.split(','))
    const columns = mergeEscapeStrings(header.split(',')).map(value => clearQuotes(value))
    const rawProduct: Record<string, string | undefined> = columns
      .map((column, index) => ({
        [column]: productTextSplited[index],
      }))
      .reduce((acc, curr) => {
        return Object.assign(acc, curr)
      }, {})

    let productData: RawProductData | undefined

    const product: Partial<RawMinimalProduct> = {
      retailer: rawProduct.retailer,
      id: rawProduct.id,
    }

    // If some of value are undefined, the format is error, just ignore it
    if (isMinimalProductBroken(product)) {
      console.error('Product raw data broken:', product)
    } else {
      productData = {
        type: 'products',
        data: [product as RawMinimalProduct],
      }
    }

    // Then continue to check, save product to products const
    const messageAfterProduct = message.slice(testTextLineEnding + 1)
    return [productData, ...splitProductsFromMessage(messageAfterProduct, header)].filter(Boolean)
  }
  return []
}
